import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import EditableImageOverlay from "./EditableImageOverlay";
import { initialOverlayRect, readOverlayFile, loadOverlayImage } from "./imageOverlays";
import { extractPdfEditableText } from "./pdfPreview";
import { rasterizeText, rotateAnnotationRect } from "./pdfAnnotations";

import { usePointerDragAutoScroll } from "../../shared/dragAutoScroll";

const noop = () => {};
const defaults = { text: "", fontSize: 18, color: "#111111", bold: false, background: false };
const rectStyle = (rect) => ({ left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.width * 100}%`, height: `${rect.height * 100}%` });

export default function PdfPageEditor({ page, sourceFile, pageIndex, pageCount, onClose, onNavigate, onRotate, onChange, onCrop = noop }) {
  const cropAutoScroll = usePointerDragAutoScroll();
  const canvasRef = useRef(null);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const textRef = useRef(null);
  const lifecycle = useRef(0);
  const cropDrag = useRef(null);
  const [cropping, setCropping] = useState(false);
  const [cropSelection, setCropSelection] = useState(null);
  const annotations = page.annotations || [];
  const latest = useRef({ annotations, onChange });
  latest.current = { annotations, onChange };
  const [zoom, setZoom] = useState(null);
  const [viewport, setViewport] = useState({ width: 900, height: 650 });
  const [selectedId, setSelectedId] = useState(null);
  const [textForm, setTextForm] = useState(null);
  const [textRuns, setTextRuns] = useState(null);
  const [pickingText, setPickingText] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const width = page.width || 595;
  const height = page.height || 842;
  const sideways = page.rotation % 180 !== 0;
  const displayWidth = sideways ? height : width;
  const displayHeight = sideways ? width : height;
  const visibleCrop = rotateAnnotationRect(page.crop || { x: 0, y: 0, width: 1, height: 1 }, page.rotation);
  const visibleWidth = displayWidth * visibleCrop.width;
  const visibleHeight = displayHeight * visibleCrop.height;
  const fitScale = Math.max(.1, Math.min((viewport.width - 32) / visibleWidth, (viewport.height - 32) / visibleHeight));
  const scale = zoom === null ? fitScale : zoom / 100;
  const percent = Math.round(scale * 100);
  const previewRotation = page.rotation - page.imageRotation;
  const previewSideways = Math.abs(previewRotation % 180) === 90;
  const selected = annotations.find((item) => item.id === selectedId);

  useEffect(() => {
    const previousFocus = document.activeElement;
    dialogRef.current?.querySelector("button")?.focus();
    const node = canvasRef.current;
    const update = () => setViewport({ width: node.clientWidth || 900, height: node.clientHeight || 650 });
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(node);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); lifecycle.current += 1; previousFocus?.focus?.(); };
  }, []);

  const commit = (next) => {
    const previous = latest.current.annotations;
    setUndoStack((stack) => [...stack.slice(-29), { annotations: previous, crop: page.crop }]);
    setRedoStack([]);
    latest.current.onChange(next);
  };
  const add = (annotation) => {
    commit([...latest.current.annotations, annotation]);
    setSelectedId(annotation.id); setPickingText(false);
  };
  const undo = () => {
    if (!undoStack.length || busy || cropping) return;
    setRedoStack((stack) => [...stack, { annotations, crop: page.crop }]);
    onChange(undoStack[undoStack.length - 1].annotations); onCrop(undoStack[undoStack.length - 1].crop); setUndoStack((stack) => stack.slice(0, -1));
    setSelectedId(null); setTextForm(null);
  };
  const redo = () => {
    if (!redoStack.length || busy || cropping) return;
    setUndoStack((stack) => [...stack, { annotations, crop: page.crop }]);
    onChange(redoStack[redoStack.length - 1].annotations); onCrop(redoStack[redoStack.length - 1].crop); setRedoStack((stack) => stack.slice(0, -1));
    setSelectedId(null); setTextForm(null);
  };
  const changeCrop = (crop) => {
    setUndoStack((stack) => [...stack.slice(-29), { annotations, crop: page.crop }]);
    cropAutoScroll.stop();
    setRedoStack([]); onCrop(crop); setCropping(false); setCropSelection(null); setZoom(null);
    setNotice(crop ? "已裁切，可繼續編輯；匯出 PDF 會保留此範圍。" : "已還原完整頁面。");
  };
  const cropPoint = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)) };
  };
  const updateCropSelection = (event) => {
    if (!cropDrag.current) return;
    const point = cropPoint(event); const start = cropDrag.current;
    setCropSelection({ x: Math.min(start.x, point.x), y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x), height: Math.abs(point.y - start.y) });
  };
  const applyCrop = () => {
    if (!cropSelection || cropSelection.width < .01 || cropSelection.height < .01) return;
    changeCrop(rotateAnnotationRect({ x: visibleCrop.x + cropSelection.x * visibleCrop.width,
      y: visibleCrop.y + cropSelection.y * visibleCrop.height,
      width: cropSelection.width * visibleCrop.width, height: cropSelection.height * visibleCrop.height }, -page.rotation));
  };
  const removeSelected = () => {
    if (!selectedId || busy) return;
    commit(annotations.filter((item) => item.id !== selectedId));
    setSelectedId(null); setTextForm(null);
  };
  const insertFiles = async (files) => {
    const images = Array.from(files).filter((file) => /^image\//.test(file.type));
    if (!images.length) return;
    const request = ++lifecycle.current;
    setBusy(true); setNotice("");
    try {
      const decoded = await Promise.all(images.map(async (file) => {
        const image = await readOverlayFile(file);
        if (!page.rotation) return image;
        const bitmap = await loadOverlayImage(image.image);
        const canvas = document.createElement("canvas");
        canvas.width = sideways ? image.height : image.width;
        canvas.height = sideways ? image.width : image.height;
        const context = canvas.getContext("2d");
        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate(-page.rotation * Math.PI / 180);
        context.drawImage(bitmap, -image.width / 2, -image.height / 2, image.width, image.height);
        return { image: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
      }));
      if (lifecycle.current !== request) return;
      const crop = page.crop || { x: 0, y: 0, width: 1, height: 1 };
      const added = decoded.map((image, index) => {
        const rect = initialOverlayRect(image, { width: width * crop.width, height: height * crop.height });
        return { id: `image-${Date.now()}-${index}`, kind: "image", label: images[index].name || "貼上的圖片", image: image.image,
          x: crop.x + rect.x * crop.width, y: crop.y + rect.y * crop.height,
          width: rect.width * crop.width, height: rect.height * crop.height };
      });
      commit([...latest.current.annotations, ...added]);
      setSelectedId(added[added.length - 1].id); setTextForm(null); setPickingText(false);
      setNotice("圖片已加入，可以拖曳移動、拉四角縮放。");
    } catch (error) { if (lifecycle.current === request) setNotice(error.message || "圖片無法讀取。"); }
    finally { if (lifecycle.current === request) setBusy(false); }
  };
  useEffect(() => {
    const paste = (event) => {
      if (busy || cropping) return;
      const files = Array.from(event.clipboardData?.items || []).filter((item) => item.kind === "file").map((item) => item.getAsFile()).filter(Boolean);
      const images = (files.length ? files : Array.from(event.clipboardData?.files || [])).filter((file) => /^image\//.test(file.type));
      if (!images.length) return;
      event.preventDefault(); event.stopPropagation(); insertFiles(images);
    };
    window.addEventListener("paste", paste);
    return () => window.removeEventListener("paste", paste);
  });

  const chooseText = (annotation) => {
    setSelectedId(annotation.id);
    setTextForm({ ...defaults, ...annotation });
    setPickingText(false);
    setTimeout(() => textRef.current?.focus(), 0);
  };
  const pickOriginalText = async () => {
    setTextForm(null); setSelectedId(null); setPickingText(true);
    if (textRuns) return;
    const request = ++lifecycle.current;
    setBusy(true); setNotice("正在讀取原有文字…");
    try {
      const runs = await extractPdfEditableText(sourceFile, page.pageIndex);
      if (lifecycle.current !== request) return;
      setTextRuns(runs);
      setNotice(runs.length ? "點選藍框內的原有文字，即可修改。" : "這頁沒有可選取文字。掃描頁請新增文字框，勾選白色底遮住舊字。");
    } catch { if (lifecycle.current === request) setNotice("原有文字無法讀取，仍可新增文字框。"); }
    finally { if (lifecycle.current === request) setBusy(false); }
  };
  const saveText = async () => {
    if (!textForm || busy) return;
    const request = ++lifecycle.current;
    const form = { ...textForm };
    setBusy(true); setNotice("");
    try {
      const textIsSideways = Math.abs(Math.sin((form.textAngle || 0) * Math.PI / 180)) > .7;
      const crop = page.crop || { x: 0, y: 0, width: 1, height: 1 };
      const lineSpace = textIsSideways ? height * (crop.y + crop.height - (form.y || 0)) : width * (crop.x + crop.width - (form.x || 0));
      const raster = await rasterizeText(form, Math.min((textIsSideways ? height * crop.height : width * crop.width) * .8, lineSpace));
      if (lifecycle.current !== request) return;
      const existing = latest.current.annotations.find((item) => item.id === form.id);
      const rect = { x: form.x ?? .15, y: form.y ?? .15,
        width: Math.min(.95, crop.width, raster.width / width), height: Math.min(.95, crop.height, raster.height / height) };
      // Explicit corner resizing is preserved when changing only the content.
      if (existing?.resized) { rect.width = existing.width; rect.height = existing.height; }
      rect.x = Math.max(crop.x, Math.min(crop.x + crop.width - rect.width, rect.x));
      rect.y = Math.max(crop.y, Math.min(crop.y + crop.height - rect.height, rect.y));
      const next = { ...form, ...rect, image: raster.image, kind: "text", label: form.text || "空白文字", id: form.id || `text-${Date.now()}` };
      if (existing) commit(latest.current.annotations.map((item) => item.id === next.id ? next : item));
      else add(next);
      setSelectedId(next.id); setTextForm(null);
    } catch (error) { if (lifecycle.current === request) setNotice(error.message || "文字無法加入。"); }
    finally { if (lifecycle.current === request) setBusy(false); }
  };
  const keyboard = (event) => {
    if (event.key === "Tab") {
      const controls = Array.from(dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea, [tabindex="0"]')).filter((node) => node.getClientRects().length);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    if (event.target.closest('input,textarea,select,[contenteditable="true"]') || event.nativeEvent?.isComposing) return;
    if (event.key === "Enter" && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (busy || cropping || textForm) return;
      event.preventDefault();
      const offset = event.shiftKey ? -1 : 1;
      if (pageIndex + offset >= 0 && pageIndex + offset < pageCount) onNavigate(offset);
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") { event.preventDefault(); if (event.shiftKey) redo(); else undo(); }
    if (selectedId && ["Delete", "Backspace"].includes(event.key)) { event.preventDefault(); removeSelected(); }
  };

  return <Backdrop onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }} onKeyDown={keyboard}>
    <Dialog ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${page.fileName} 第 ${page.sourcePageNumber} 頁放大預覽`}>
      <Header><div><strong>{page.fileName}</strong><small>原第 {page.sourcePageNumber} 頁 · 編輯保留於本次工作階段</small></div><Button aria-label="關閉放大預覽" onClick={onClose}>✕</Button></Header>
      <Controls aria-label="放大預覽功能">
        <Button aria-label="預覽上一頁" disabled={pageIndex <= 0 || busy || cropping || !!textForm} onClick={() => onNavigate(-1)}>← 上一頁</Button>
        <span>{pageIndex + 1} / {pageCount}</span>
        <Button aria-label="預覽下一頁" disabled={pageIndex >= pageCount - 1 || busy || cropping || !!textForm} onClick={() => onNavigate(1)}>下一頁 →</Button>
        <Button aria-label="預覽頁面左轉" disabled={busy || cropping} onClick={() => onRotate(-90)}>↶ 左轉</Button>
        <Button aria-label="預覽頁面右轉" disabled={busy || cropping} onClick={() => onRotate(90)}>↷ 右轉</Button>
        <Button onClick={() => setZoom(null)} aria-pressed={zoom === null}>適合視窗</Button>
        <Button onClick={() => setZoom(100)}>原始大小</Button>
        <Button aria-label="縮小 PDF" disabled={percent <= 25} onClick={() => setZoom(Math.max(25, percent - 25))}>−</Button>
        <output aria-label="PDF 縮放比例">{percent}%</output>
        <input aria-label="調整 PDF 縮放" type="range" min="25" max="400" step="5" value={Math.max(25, Math.min(400, percent))} onChange={(event) => setZoom(Number(event.target.value))} />
        <Button aria-label="放大 PDF" disabled={percent >= 400} onClick={() => setZoom(Math.min(400, percent + 25))}>＋</Button>
      </Controls>
      <Controls>
        <Button disabled={busy || !!textForm} aria-pressed={cropping} onClick={() => { cropAutoScroll.stop(); setCropping(!cropping); setCropSelection(null); setSelectedId(null); setPickingText(false); setNotice("在頁面上按住滑鼠拖曳，框選要保留的範圍，再按「套用裁切」。"); }}>裁切頁面</Button>
        {cropping && <><Button disabled={!cropSelection || cropSelection.width < .01 || cropSelection.height < .01} onClick={applyCrop}>套用裁切</Button><Button onClick={() => { cropAutoScroll.stop(); setCropping(false); setCropSelection(null); setNotice(""); }}>取消裁切</Button></>}
        {page.crop && <Button disabled={busy || cropping} onClick={() => changeCrop(undefined)}>還原完整頁面</Button>}
        <Button disabled={busy || cropping} onClick={() => { setSelectedId(null); setTextForm({ ...defaults, ...(page.crop ? { x: page.crop.x + page.crop.width * .15, y: page.crop.y + page.crop.height * .15 } : {}), textAngle: -page.rotation }); setPickingText(false); setTimeout(() => textRef.current?.focus(), 0); }}>＋ 新增文字</Button>
        <Button disabled={busy || cropping} aria-pressed={pickingText} onClick={pickOriginalText}>修改原有文字</Button>
        <Button disabled={busy || cropping} onClick={() => inputRef.current?.click()}>加入圖片</Button>
        <input ref={inputRef} aria-label="選擇覆蓋圖片" type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(event) => { insertFiles(event.target.files); event.target.value = ""; }} />
        <Button disabled={!selected || selected.kind !== "text" || busy || cropping} onClick={() => chooseText(selected)}>編輯文字框</Button>
        <Button disabled={!selected || busy || cropping} onClick={removeSelected}>刪除選取內容</Button>
        <Button disabled={!undoStack.length || busy || cropping} onClick={undo}>復原</Button>
        <Button disabled={!redoStack.length || busy || cropping} onClick={redo}>重做</Button>
        <small>Enter 下一頁 · Shift + Enter 上一頁 · ⌘ / Ctrl + V 貼圖片 · 拖曳移動 · 拉四角縮放 · 方向鍵微調</small>
      </Controls>
      {textForm && <TextPanel>
        <label>文字內容<textarea ref={textRef} aria-label="文字內容" maxLength={10000} value={textForm.text} onChange={(event) => setTextForm({ ...textForm, text: event.target.value })} /></label>
        <label>字級<input aria-label="文字字級" type="number" min="6" max="144" value={textForm.fontSize} onChange={(event) => setTextForm({ ...textForm, fontSize: event.target.value })} /></label>
        <label>顏色<input aria-label="文字顏色" type="color" value={textForm.color} onChange={(event) => setTextForm({ ...textForm, color: event.target.value })} /></label>
        <label><input type="checkbox" checked={textForm.bold} onChange={(event) => setTextForm({ ...textForm, bold: event.target.checked })} />粗體</label>
        <label><input type="checkbox" checked={textForm.background} onChange={(event) => setTextForm({ ...textForm, background: event.target.checked })} />白色底</label>
        <Button disabled={busy || cropping} onClick={saveText}>套用文字</Button><Button disabled={busy || cropping} onClick={() => setTextForm(null)}>取消</Button>
      </TextPanel>}
      <Hint role="status">{notice || "修改會套用到 PDF 與 PNG 匯出。"} 原有文字以白底覆蓋，再加入新字；不會移除底層原文。新字匯出為圖片。</Hint>
      <Canvas ref={canvasRef} onClick={() => setSelectedId(null)}>
        <Stage data-testid="pdf-editor-stage" style={{ width: visibleWidth * scale, height: visibleHeight * scale, overflow: "hidden" }}>
          <div style={{ position: "absolute", width: displayWidth * scale, height: displayHeight * scale, left: -visibleCrop.x * displayWidth * scale, top: -visibleCrop.y * displayHeight * scale }}>
          <Base data-preview-base src={page.image} alt={`${page.fileName} 第 ${page.sourcePageNumber} 頁放大預覽`} draggable={false} style={{ position: "absolute", left: "50%", top: "50%", width: previewSideways ? `${displayHeight / displayWidth * 100}%` : "100%", height: previewSideways ? `${displayWidth / displayHeight * 100}%` : "100%", transform: `translate(-50%, -50%) rotate(${previewRotation}deg)` }} />
          {annotations.filter((item) => item.originalRect).map((item) => <Mask key={`mask-${item.id}`} style={rectStyle(rotateAnnotationRect(item.originalRect, page.rotation))} />)}
          {pickingText && (textRuns || []).filter((run) => !annotations.some((item) => item.sourceTextId === run.id)).map((run) => <TextTarget key={run.id} aria-label={`修改文字：${run.text}`} title={run.text} style={rectStyle(rotateAnnotationRect(run, page.rotation))} onClick={(event) => {
            event.stopPropagation(); setTextForm({ ...defaults, ...run, id: `replacement-${run.id}`, sourceTextId: run.id, originalRect: { x: run.x, y: run.y, width: run.width, height: run.height }, background: true }); setPickingText(false); setTimeout(() => textRef.current?.focus(), 0);
          }} />)}
          {annotations.map((item) => <EditableImageOverlay key={item.id} overlay={rotateAnnotationRect(item, page.rotation)} selected={selectedId === item.id} disabled={busy || cropping || page.loading || !!textForm} onInteracting={noop}
            accessibleLabel={item.kind === "text" ? `文字框：${item.text}` : undefined}
            onSelect={() => { setSelectedId(item.id); setPickingText(false); }}
            onCommit={(next) => { const canonical = rotateAnnotationRect(next, 360 - page.rotation); commit(annotations.map((old) => old.id === item.id ? { ...canonical, resized: old.resized || Math.abs(canonical.width - old.width) > .00001 || Math.abs(canonical.height - old.height) > .00001 } : old)); }}
            imageStyle={(current) => ({ position: "absolute", left: "50%", top: "50%", width: sideways ? `${current.height * displayHeight / (current.width * displayWidth) * 100}%` : "100%", height: sideways ? `${current.width * displayWidth / (current.height * displayHeight) * 100}%` : "100%", transform: `translate(-50%, -50%) rotate(${page.rotation}deg)` })} />)}
          </div>
          {cropping && <CropLayer aria-label="框選裁切範圍" onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault(); event.stopPropagation(); event.currentTarget.setPointerCapture?.(event.pointerId);
            cropDrag.current = cropPoint(event); setCropSelection(null); cropAutoScroll.start(event);
          }} onPointerMove={updateCropSelection} onPointerUp={(event) => { updateCropSelection(event); cropDrag.current = null; }} onPointerCancel={() => { cropDrag.current = null; setCropSelection(null); }}>
            {cropSelection && <CropBox style={rectStyle(cropSelection)} />}
          </CropLayer>}
        </Stage>
        {page.loading && <Loading>準備清晰預覽…</Loading>}
      </Canvas>
    </Dialog>
  </Backdrop>;
}

const Backdrop = styled.div`position:fixed;inset:0;z-index:10000;background:var(--app-modal-overlay, rgba(64, 64, 64, 0.72));display:grid;place-items:center;padding:8px;`;
const Dialog = styled.div`width:min(1600px,calc(100vw - 16px));height:calc(100dvh - 16px);display:flex;flex-direction:column;overflow:hidden;background:var(--app-surface,#fff);color:var(--app-text,#222);border-radius:12px;box-shadow:0 15px 60px #0006;`;
const Header = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 16px;border-bottom:1px solid #ddd;div{min-width:0;display:grid;gap:3px;}strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}small{color:#747474;}`;
const Button = styled.button`border:1px solid var(--app-border,#ccc);border-radius:7px;padding:8px 10px;background:var(--app-surface,#fff);color:inherit;font:inherit;font-size:13px;cursor:pointer;white-space:nowrap;&:disabled{opacity:.4;cursor:default;}&[aria-pressed=true]{border-color:#2563eb;background:#2563eb18;}`;
const Controls = styled.div`display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:7px;padding:8px 12px;background:var(--app-surface-soft,#f6f6f6);border-bottom:1px solid #ddd;input[type=range]{width:100px;}small{font-size:12px;color:#666;}`;
const TextPanel = styled.div`display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding:10px 16px;max-height:30vh;overflow:auto;label{display:flex;align-items:center;gap:5px;font-size:13px;}label:first-child{flex:1;min-width:220px;}textarea{width:100%;min-height:55px;font:inherit;}input[type=number]{width:60px;}input[type=color]{width:36px;} `;
const Hint = styled.div`padding:5px 16px;font-size:12px;color:var(--app-muted-text,#666);line-height:1.5;`;
const Canvas = styled.div`position:relative;flex:1;min-height:80px;overflow:auto;padding:16px;background:#404040;overscroll-behavior:contain;`;
const Stage = styled.div`position:relative;flex:none;margin:auto;background:white;box-shadow:0 2px 12px #0005;`;
const Base = styled.img`display:block;width:100%;height:100%;pointer-events:none;user-select:none;`;
const Mask = styled.div`position:absolute;background:white;pointer-events:none;`;
const TextTarget = styled.button`position:absolute;padding:0;border:1px solid #2563eb80;background:#2563eb0d;cursor:text;&:hover,&:focus{background:#2563eb35;border-color:#2563eb;}`;
const Loading = styled.div`position:sticky;bottom:8px;width:fit-content;margin:8px auto;background:#fff;padding:8px 12px;border-radius:6px;color:#333;`;

const CropLayer = styled.div`position:absolute;inset:0;z-index:20;cursor:crosshair;touch-action:none;user-select:none;`;
const CropBox = styled.div`position:absolute;border:2px solid #2563eb;box-sizing:border-box;background:#2563eb12;box-shadow:0 0 0 10000px #0008;pointer-events:none;`;
